import React, { useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import Badge      from "@react/shared/ui/Badge"
import EmptyState from "@react/shared/ui/EmptyState"
import { useDropdownMenu, MenuPanel, MENU_ITEM } from "@react/shared/ui/DropdownMenu"
import { getRequest } from "@react/helpers/fetch"
import { formatMoney } from "./StatsStrip"
import QuoteRowMenu from "./QuoteRowMenu"

const GRID_COLUMNS = "120px minmax(180px, 1fr) minmax(220px, 1.5fr) 140px 190px 110px 76px"

const STATUS_TONE = {
  "In preparation":     "brand",
  "Demand":             "orange",
  "Sent":               "warning",
  "Rejected":           "neutral",
  "Accepted":           "success",
  "Partially invoiced": "blue",
  "Invoiced":           "blue",
}

const formatDate = iso => {
  if (!iso) return "—"
  const date = new Date(iso)
  if (isNaN(date.getTime())) return "—"
  return date.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" })
}

const SortHeader = ({ label, column, order, direction, onSortChange, align = "left" }) => {
  const active = order === column
  const nextDirection = active && direction === "asc" ? "desc" : "asc"
  const justify = align === "right" ? "justify-end" : align === "center" ? "justify-center" : "justify-start"
  return (
    <button
      type      = "button"
      onClick   = {() => onSortChange(column, nextDirection)}
      className = {`flex items-center gap-1 ${justify} uppercase tracking-wider text-[11px] font-medium ${active ? "text-slate-700" : "text-slate-500 hover:text-slate-700"}`}
    >
      {label}
      {active && <FontAwesomeIcon icon={direction === "asc" ? "arrow-up" : "arrow-down"} className="text-[9px]" />}
    </button>
  )
}

const VERSIONS_MENU_WIDTH = 340

const VersionsBadge = ({ quote }) => {
  const { open, close, toggle, pos, btnRef, menuRef } = useDropdownMenu({ width: VERSIONS_MENU_WIDTH })
  const [versions, setVersions] = useState(null)

  const onToggle = e => {
    toggle(e)
    if (open) return
    getRequest(quote.versions_url).then(setVersions).catch(() => setVersions([]))
  }

  return (
    <>
      <button
        ref       = {btnRef}
        type      = "button"
        onClick   = {onToggle}
        className = "shrink-0 bg-transparent border-0 p-0 cursor-pointer"
        title     = {quote.version > 0 ? I18n.t("quotes.project_index.table.version", { number: quote.version }) : I18n.t("quotes.bigcard.versions")}
      >
        <Badge tone="brand" variant="soft" size="xs" className="hover:ring-1 hover:ring-current">
          {quote.version > 0 ? `v${quote.version}` : <FontAwesomeIcon icon="code-branch" />}
        </Badge>
      </button>

      {open && (
        <MenuPanel menuRef={menuRef} pos={pos} width={VERSIONS_MENU_WIDTH}>
          <div onClick={e => e.stopPropagation()}>
            <div className="px-2.5 pt-1 pb-1.5 text-[11px] uppercase tracking-wider font-medium text-slate-500">
              {I18n.t("quotes.bigcard.versions")}
            </div>
            {versions === null ? (
              <div className="px-2.5 py-1.5 text-slate-500"><FontAwesomeIcon icon="spinner" spin /></div>
            ) : versions.map(version => (
              <a
                key          = {version.id}
                href         = {version.edit_url}
                onClick      = {close}
                aria-current = {version.current ? "page" : undefined}
                className    = {`${MENU_ITEM} ${version.current ? "bg-slate-50" : ""}`}
              >
                <span className="w-6 shrink-0 font-semibold text-slate-900">v{version.version}</span>
                <span className="flex-1 min-w-0 truncate font-mono text-[12px]">{version.number}</span>
                <Badge tone={STATUS_TONE[version.status] || "neutral"} variant="soft" size="xs" className="shrink-0">
                  {version.status_label}
                </Badge>
                <span className="shrink-0 tabular-nums text-slate-900">{formatMoney(version.subtotal_cents, version.currency_symbol)}</span>
              </a>
            ))}
          </div>
        </MenuPanel>
      )}
    </>
  )
}

const rowTarget = quote => (quote.import_running || quote.import_awaiting || quote.destroying ? null : quote.edit_url)

// A quote still being imported has no subtotal yet, and does not open.
// As on the classic small card: no badge for a draft or a quote outside any workflow.
const ValidationBadge = ({ quote, onValidation }) => {
  const badge = quote.validation
  if (!badge || ["draft", "not_applicable"].includes(badge.status)) return null
  return (
    <button
      type      = "button"
      onClick   = {event => { event.stopPropagation(); onValidation?.(quote) }}
      title     = {I18n.t("validation.workflow.modal_title")}
      className = "inline-flex rounded-full focus:outline-none focus:ring-2 focus:ring-brand-500/30"
    >
      <Badge tone={badge.tone} variant="soft" size="xs">
        <FontAwesomeIcon icon="user-check" className="mr-1" />
        {badge.label}
      </Badge>
    </button>
  )
}

const QuoteRow = ({ quote, onValidation, onConfigureImport, onCreateProject, onDuplicate, onNewVersion, onSaveAsTemplate, onDelete }) => (
  <div
    role      = "row"
    title     = {quote.can_edit === false ? I18n.t("quotes.project_index.table.locked_hint", { defaultValue: "Devis transformé en chantier : il n'est plus modifiable" }) : undefined}
    className = {`group grid items-center gap-x-3 px-4 py-3 border-b border-slate-100 last:border-b-0 ${quote.destroying ? "bg-slate-50/60 opacity-50" : quote.import_running ? "bg-slate-50/60" : "hover:bg-slate-50 cursor-pointer"}`}
    style     = {{ gridTemplateColumns: GRID_COLUMNS }}
    onClick   = {() => {
      if (quote.destroying) return
      if (quote.import_awaiting) return onConfigureImport(quote)
      const target = rowTarget(quote)
      if (target) window.location.href = target
    }}
  >
    <div className="flex items-center gap-1.5 min-w-0">
      <Badge tone="neutral" variant="soft" size="xs" className="font-mono max-w-full truncate">
        {quote.number || `Q-${quote.id}`}
      </Badge>
      {quote.versions_url && <VersionsBadge quote={quote} />}
      {quote.project_id && (
        <FontAwesomeIcon
          icon      = "hard-hat"
          className = "shrink-0 text-amber-500 text-xs"
          title     = {I18n.t("quotes.project_index.table.has_project")}
        />
      )}
      {quote.can_edit === false && (
        <FontAwesomeIcon
          icon      = "lock"
          className = "shrink-0 text-slate-400 text-[10px]"
          title     = {I18n.t("quotes.project_index.table.locked_hint", { defaultValue: "Devis transformé en chantier : il n'est plus modifiable" })}
        />
      )}
    </div>

    <div className="min-w-0">
      <div className="font-medium text-slate-900 truncate" title={quote.client_name}>
        {quote.client_name || "—"}
      </div>
      {quote.manager_name && (
        <div className="text-xs text-slate-500 truncate" title={quote.manager_name}>{quote.manager_name}</div>
      )}
    </div>

    <div className="min-w-0 text-sm text-slate-700 truncate" title={quote.title}>
      {quote.title || "—"}
    </div>

    <div className="text-right tabular-nums font-semibold text-slate-900 whitespace-nowrap">
      {quote.import_running || quote.import_awaiting ? "—" : formatMoney(quote.subtotal_cents, quote.currency_symbol)}
    </div>

    <div className="min-w-0">
      {quote.destroying ? (
        <Badge tone="neutral" variant="soft" size="xs" className="max-w-full truncate">
          <FontAwesomeIcon icon="spinner" spin className="mr-1" />
          {I18n.t("quotes.project_index.table.destroying")}
        </Badge>
      ) : quote.import_running ? (
        <Badge tone="brand" variant="soft" size="xs" className="max-w-full truncate" title={quote.import_message}>
          <FontAwesomeIcon icon="spinner" spin className="mr-1" />
          {quote.import_message}
        </Badge>
      ) : quote.import_awaiting ? (
        <Badge tone="warning" variant="soft" size="xs" className="max-w-full truncate" title={quote.import_message}>
          <FontAwesomeIcon icon="sliders" className="mr-1" />
          {quote.import_message}
        </Badge>
      ) : quote.import_failed ? (
        <Badge tone="danger" variant="soft" size="xs" className="max-w-full truncate" title={quote.import_message}>
          <FontAwesomeIcon icon="triangle-exclamation" className="mr-1" />
          {quote.import_message}
        </Badge>
      ) : (
        <div className="flex flex-wrap items-center gap-1">
          <Badge tone={STATUS_TONE[quote.status] || "neutral"} variant="soft" size="xs">
            {quote.status_label}
          </Badge>
          {quote.signed && (
            <Badge tone="success" variant="soft" size="xs" title={I18n.t("quotes.project_index.table.signed")}>
              <FontAwesomeIcon icon="signature" className="mr-1" />
              {I18n.t("quotes.project_index.table.signed")}
            </Badge>
          )}
          {quote.expired && (
            <Badge tone="danger" variant="soft" size="xs">
              <FontAwesomeIcon icon="hourglass-end" className="mr-1" />
              {I18n.t("quotes.project_index.table.expired")}
            </Badge>
          )}
          <ValidationBadge quote={quote} onValidation={onValidation} />
        </div>
      )}
    </div>

    <div className="text-sm text-slate-600 tabular-nums whitespace-nowrap">
      {formatDate(quote.creation_date)}
      {quote.expiration_date && quote.status === "Sent" && (
        <div className={`text-xs ${quote.expired ? "text-rose-600" : "text-slate-400"}`} title={I18n.t("quotes.project_index.table.expires_on")}>
          → {formatDate(quote.expiration_date)}
        </div>
      )}
    </div>

    <div className="flex items-center justify-end" onClick={event => event.stopPropagation()}>
      {!quote.destroying && <QuoteRowMenu
        quote            = {quote}
        onValidation     = {onValidation}
        onCreateProject  = {onCreateProject}
        onDuplicate      = {onDuplicate}
        onNewVersion     = {onNewVersion}
        onSaveAsTemplate = {onSaveAsTemplate}
        onDelete         = {onDelete}
      />}
    </div>
  </div>
)

const HeaderRow = ({ order, direction, onSortChange }) => (
  <div
    role      = "row"
    className = "grid items-center gap-x-3 px-4 py-3 border-b border-slate-200"
    style     = {{ gridTemplateColumns: GRID_COLUMNS }}
  >
    <SortHeader label={I18n.t("quotes.project_index.table.number")}   column="quotes.number"        order={order} direction={direction} onSortChange={onSortChange} />
    <SortHeader label={I18n.t("quotes.project_index.table.client")}   column="clients.display_name" order={order} direction={direction} onSortChange={onSortChange} />
    <SortHeader label={I18n.t("quotes.project_index.table.title")}    column="quotes.title"         order={order} direction={direction} onSortChange={onSortChange} />
    <SortHeader label={I18n.t("quotes.project_index.table.subtotal")} column="quotes.subtotal_cents" order={order} direction={direction} onSortChange={onSortChange} align="right" />
    <SortHeader label={I18n.t("quotes.project_index.table.status")}   column="quotes.status"        order={order} direction={direction} onSortChange={onSortChange} />
    <SortHeader label={I18n.t("quotes.project_index.table.date")}     column="quotes.creation_date" order={order} direction={direction} onSortChange={onSortChange} />
    <div />
  </div>
)

const QuotesTable = ({ quotes, order, direction, onSortChange, onValidation, onConfigureImport, onCreateProject, onDuplicate, onNewVersion, onSaveAsTemplate, onDelete }) => (
  <div className="overflow-x-auto" role="table">
    <HeaderRow order={order} direction={direction} onSortChange={onSortChange} />
    {quotes.map(quote => (
      <QuoteRow
        key               = {quote.id}
        quote             = {quote}
        onValidation      = {onValidation}
        onConfigureImport = {onConfigureImport}
        onCreateProject  = {onCreateProject}
        onDuplicate      = {onDuplicate}
        onNewVersion     = {onNewVersion}
        onSaveAsTemplate = {onSaveAsTemplate}
        onDelete         = {onDelete}
      />
    ))}
    {quotes.length === 0 && (
      <EmptyState size="lg">{I18n.t("quotes.project_index.table.empty")}</EmptyState>
    )}
  </div>
)

export default QuotesTable
